import React, { useContext, useEffect, useState } from "react";
import ServiceWrapper from "../../../../Services/ServiceWrapper";
import AuthManager from "../../../../Common/AuthManager";
import AppSettings from "../../../../Settings/AppSettings";
import { CategoryContext } from "../CategoryContext";
import { List, arrayMove } from "react-movable";
import { toast } from "Components/DesignSystem/Toast/useToast";
import { AppContext } from "../../../../Context/AppContext";
import LoadingSuccessErrorAlert from "./LoadingSuccessErrorAlert";
import Accordion from "Components/DesignSystem/Accordion/Accordion";
import Button from "Components/DesignSystem/Button/Button";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import Typography from "Components/DesignSystem/Typography/Typography";
import { Drag_n_drop } from "Components/DesignSystem/Iconography/Icons";

/**
 * Relevant Categories — on the design system since the category module's
 * Phase 5. Every service call, payload shape ({ id, rank }), permission check,
 * JSON-compare dirty gate and the stale-closure 5000ms timer are the legacy
 * logic verbatim; only the rendering changed: DS Accordion (controlled, § 192
 * actions slot), react-movable KEPT for the reorder list with rows restyled on
 * role tokens, and the § 99 status strip.
 */
const RelevantCategories = () => {
  let api_base_url = AppSettings.GMS_BACKEND_API_URL;
  const categoryTypeContextData = useContext(CategoryContext);
  const [loading, setLoading] = useState({
    status: "",
    message: "",
  });
  const appData = useContext(AppContext);
  const [list, setList] = useState(null);
  const [EditPage, setEditPage] = useState(false);
  const [collapse, setCollapse] = useState(false);
  let [relevantCategories, setRelevantCategories] = useState(null);
  useEffect(() => {
    setRelevantCategories(null);
    setLoading({
      status: "",
      message: "",
    });
  }, [categoryTypeContextData.editingItem.id]);

  useEffect(() => {
    if (list === null && collapse) {
      load_associated_rel_cat();
    }
  }, [collapse]);
  useEffect(() => {
    if (EditPage) {
      load_associated_rel_cat();
    }
  }, [EditPage]);

  async function load_associated_rel_cat() {
    setLoading({ status: "Loading", message: "Loading Relevant Categories" });
    let res = await ServiceWrapper.doGet(
      `${api_base_url}category/${categoryTypeContextData.editingItem.id}/relevantcategories/list`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound) {
      setLoading({ status: "Success", message: undefined });
      setRelevantCategories(res.data.data);
      setList(res.data.data);
    } else {
      setLoading({ status: "Error", message: "Error while loading details" });
    }
  }

  function handle_collapse() {
    setCollapse(!collapse);
  }

  async function handle_save() {
    setLoading({
      status: "Loading",
      message: "Saving new order for Relevant Categories",
    });
    let newOrder = [];
    list.map((item, index) => {
      newOrder.push({
        id: item.id,
        rank: `${index + 1}`,
      });
    });

    let res = await ServiceWrapper.doPost(
      `${api_base_url}category/${categoryTypeContextData.editingItem.id}/relevantcategories/edit`,
      newOrder,
      null,
      AuthManager.getGmsToken()
    );

    if (!res.errorfound) {
      if (res.data.done) {
        setLoading({
          status: "Success",
          message: res.data.message,
        });
        setEditPage(false);
        load_associated_rel_cat();
      } else {
        setLoading({
          status: "Error",
          message: res.data.message,
        });
      }
    } else {
      setLoading({
        status: "Error",
        message: "Error while saving, Please try after some time.",
      });
    }
    setTimeout(() => {
      if (loading.status !== "Success")
        setLoading({
          status: "",
          message: "",
        });
    }, 5000);
  }

  function handle_cancel() {
    setEditPage(false);
    setList(relevantCategories);
  }

  /* Header-row actions (§ 192): the legacy floated these OVER the header with
     zIndex + marginTop:-34px; same render conditions, same handlers. */
  const headerActions =
    collapse && loading.status === "Success" ? (
      !EditPage ? (
        relevantCategories && relevantCategories.length > 1 ? (
          <Button
            variant="subtle"
            size="sm"
            data-testid="category-relevant-categories-edit-btn"
            onClick={() => {
              if (appData.pm.checkPermission("gmscategory_update")) {
                setEditPage(true);
              } else {
                toast.info(
                  "You don't have permission to edit relevant category"
                );
              }
            }}
          >
            Edit
          </Button>
        ) : undefined
      ) : (
        <>
          <Button
            variant="subtle"
            size="sm"
            data-testid="category-relevant-categories-reset-btn"
            onClick={() => setList(relevantCategories)}
            disabled={
              JSON.stringify(relevantCategories) === JSON.stringify(list)
            }
          >
            Reset
          </Button>
          <Button
            variant="secondary"
            size="sm"
            data-testid="category-relevant-categories-cancel-btn"
            onClick={() => {
              handle_cancel();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="category-relevant-categories-save-btn"
            disabled={
              JSON.stringify(relevantCategories) === JSON.stringify(list)
            }
            onClick={() => handle_save()}
          >
            Save
          </Button>
        </>
      )
    ) : undefined;

  return (
    <Accordion
      title={`${EditPage ? "Update " : ""}Relevant Categories`}
      open={collapse}
      onOpenChange={() => handle_collapse()}
      actions={headerActions}
      data-testid="components-relevantcategories-div-1"
      variant="boxed"
    >
      <div className="tw-pb-4">
        <LoadingSuccessErrorAlert
          loading={loading}
          data-testid="category-relevant-categories-status"
        />

        {loading.status === "Success" && (
          <>
            {EditPage ? (
              <div className="tw-pt-3">
                {/* Typo "Relavant" is shipped copy — pinned by tests; keep it. */}
                <Typography
                  variant="sub-title-label"
                  as="h3"
                  className="tw-mb-2 tw-text-ink-secondary"
                >
                  Relavant Categories Order Ranking
                </Typography>
                <table className="tw-w-full">
                  <List
                    values={list}
                    onChange={({ oldIndex, newIndex }) =>
                      setList(arrayMove(list, oldIndex, newIndex))
                    }
                    renderList={({ children, props }) => (
                      <tbody {...props}>{children}</tbody>
                    )}
                    renderItem={({ value, props }) => {
                      const { key, ...restProps } = props;
                      return (
                        <tr key={key} className="tw-bg-surface">
                          <td className="tw-py-1" {...restProps}>
                            <span className="tw-flex tw-items-center tw-gap-2 tw-rounded-ctl tw-bg-surface-sunken tw-px-2 tw-py-1.5 tw-text-body-primary tw-text-ink tw-cursor-grab">
                              <Drag_n_drop
                                aria-hidden="true"
                                className="tw-shrink-0 tw-text-ink-tertiary"
                              />
                              {key !== undefined && `${key + 1}. `}
                              {value.name}
                            </span>
                          </td>
                        </tr>
                      );
                    }}
                    transitionDuration={100}
                    lockVertically={true}
                  />
                </table>
              </div>
            ) : (
              <div className="tw-pt-3">
                {relevantCategories && relevantCategories.length > 0 ? (
                  <div className="tw-space-y-1">
                    {relevantCategories.map((item, index) => {
                      let relevantCategory = `${index + 1}  ${item.name}`;
                      return (
                        <Typography
                          key={index}
                          variant="body-primary"
                          className="tw-text-ink-secondary"
                        >
                          {relevantCategory}
                        </Typography>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState size="sm" title="Relevant Categories Not Found" />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Accordion>
  );
};
export default RelevantCategories;
